/**
 * A dropdown consists of a toggle button and a panel.
 * <div>
 *   <button data-js-dropdown aria-controls="panel-id" aria-expanded="false" />
 *   <div id="panel-id" class="hidden" />
 * </div>
 *
 * The button and panel have the following optional data attributes:
 * data-open-classes="css-class-to-use-when-expanded other-class"
 * data-closed-classes="css-class-to-use-when-collapsed third-class"
 */
export const dropdown = () => {
  document.querySelectorAll("[data-js-dropdown]").forEach(dropdownToggleButton => {
    if (!dropdownToggleButton.getAttribute("aria-controls")) {
      console.warn("Dropdown toggle button is missing an aria-controls attribute");
      return;
    }

    const dropdownPanel = findPanel(dropdownToggleButton);
    if (!dropdownPanel) {
      return;
    }

    dropdownToggleButton.addEventListener("click", handleButtonClick);
    // prevent taking focus on mousedown (which interferes with focusout)
    dropdownToggleButton.addEventListener("mousedown", e => e.preventDefault());

    dropdownPanel.tabIndex = -1;
    dropdownPanel.addEventListener("focusout", e => handleFocusOut(e as FocusEvent));
    dropdownPanel.addEventListener("keydown", e => handleKeyDown(e as KeyboardEvent));
  });
};

const MAX_HEIGHT = 300;

const handleKeyDown = (e: KeyboardEvent) => {
  if (e.key === "Escape" || e.key === " ") {
    e.stopPropagation();
    e.preventDefault();
    const panel = e.currentTarget as HTMLElement;
    closePanel(panel);
  }
};

const handleFocusOut = (e: FocusEvent) => {
  const panel = e.currentTarget as HTMLElement;
  const focusedElement = e.relatedTarget as HTMLElement | null;
  if (!focusedElement || (panel && !panel.contains(focusedElement))) {
    closePanel(panel);
  }
};

const handleButtonClick = (e: Event) => {
  const toggleButton = e.currentTarget as HTMLElement;
  const dropdownPanel = findPanel(toggleButton);
  if (!dropdownPanel) {
    return;
  }

  const isOpen = toggleButton.getAttribute("aria-expanded") === "true";
  if (isOpen) {
    closeDropdown(toggleButton, dropdownPanel);
  } else {
    openDropdown(toggleButton, dropdownPanel);
  }
};

const findPanel = (toggleButton: Element) => {
  const panelId = toggleButton.getAttribute("aria-controls");
  if (!panelId) {
    console.warn("Dropdown toggle button is missing a panel ID");
    return null;
  }

  const dropdownPanel = document.getElementById(panelId);
  if (!dropdownPanel) {
    console.warn(`Dropdown panel with ID ${panelId} does not exist`);
    return null;
  }

  return dropdownPanel as HTMLElement | null;
};

const findToggleButton = (panel: Element) => {
  const button = document.querySelector(`[aria-controls="${panel.id}"]`);
  if (!button) {
    console.warn(`Dropdown panel "${panel.id}" is missing a toggle button`);
  }

  return button as HTMLElement | null;
};

const closePanel = (panel: HTMLElement) => {
  const button = findToggleButton(panel);
  if (button) {
    closeDropdown(button, panel);
  }
};

const closeDropdown = (button: HTMLElement, panel: HTMLElement) => {
  const openClassNames = button.dataset.openClasses ?? "";
  const closedClassNames = button.dataset.closedClasses ?? "";
  replaceClasses(button, openClassNames, closedClassNames);
  button.setAttribute("aria-expanded", "false");
  panel.removeAttribute("style");
  const panelOpenClassNames = panel.dataset.openClasses ?? "";
  const panelClosedClassNames = panel.dataset.closedClasses ?? "";
  replaceClasses(panel, panelOpenClassNames, panelClosedClassNames);
};

const openDropdown = (button: HTMLElement, panel: HTMLElement) => {
  const buttonOpenClassNames = button.dataset.openClasses ?? "";
  const buttonClosedClassNames = button.dataset.closedClasses ?? "";
  replaceClasses(button, buttonClosedClassNames, buttonOpenClassNames);
  button.setAttribute("aria-expanded", "true");
  if (isDirectionUp(button)) {
    panel.style.bottom = "100%";
    // lower z-index to avoid overshadowing the button
    panel.style.zIndex = "1"; // assumed to be lower than the toggle button
  }

  const panelOpenClassNames = panel.dataset.openClasses ?? "";
  const panelClosedClassNames = panel.dataset.closedClasses ?? "";
  replaceClasses(panel, panelClosedClassNames, panelOpenClassNames);
  if (!panelClosedClassNames) {
    panel.style.display = "block";
  }

  panel.focus(); // can't set focus until visible, will close on focusout
};

const isDirectionUp = (button: HTMLElement) => {
  const specifiedMaxHeight = Number(button.dataset.maxHeight);
  const maxHeight = specifiedMaxHeight > 0 ? specifiedMaxHeight : MAX_HEIGHT;

  const btnRect = button?.getBoundingClientRect();
  if (btnRect) {
    const spaceAbove = btnRect.top;
    const spaceBelow = window.innerHeight - btnRect.bottom;
    return spaceAbove > spaceBelow && spaceBelow < maxHeight + 16;
  }

  return false;
};

const replaceClasses = (element: HTMLElement, from: string, to: string) => {
  element.classList.remove(...from.split(" ").filter(str => !!str));
  element.classList.add(...to.split(" ").filter(str => !!str));
};
